import { useCheatsheet, useHoverContext } from "../cheatsheets/provider";
import type { CheatsheetCtx } from "../cheatsheets/types";

interface Props {
  ctx: CheatsheetCtx;
  /**
   * Absolute top/right offset in px. Defaults suit a panel header or a
   * KPI card — tune per-mount if you need it snugger.
   */
  top?: number;
  right?: number;
  /** Passes through so the card container can spread hover bindings. */
  inline?: boolean;
}

/**
 * Small ⌨ glyph that opens the cheatsheet drawer. Designed to sit in the
 * top-right corner of any card/panel/alert without touching layout.
 */
export function CheatsheetButton({ ctx, top = 6, right = 8, inline = false }: Props) {
  const { open } = useCheatsheet();
  const hover = useHoverContext(ctx);

  const positionStyle: React.CSSProperties = inline
    ? {}
    : { position: "absolute", top, right };

  return (
    <button
      {...hover}
      type="button"
      aria-label="Open troubleshooting cheatsheet"
      title="Troubleshooting cheatsheet (? or /)"
      onClick={(e) => {
        e.stopPropagation();
        open(ctx);
      }}
      style={{
        ...positionStyle,
        width: 22,
        height: 20,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        border: "1px solid transparent",
        borderRadius: 4,
        color: "var(--fg-3)",
        fontSize: 12,
        lineHeight: 1,
        transition: "background 120ms, color 120ms, border-color 120ms",
        zIndex: 5,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(78,225,240,0.10)";
        e.currentTarget.style.color = "var(--cyan)";
        e.currentTarget.style.borderColor = "rgba(78,225,240,0.25)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = "var(--fg-3)";
        e.currentTarget.style.borderColor = "transparent";
      }}
    >
      <svg width="12" height="10" viewBox="0 0 14 11" fill="none" aria-hidden="true">
        {/* compact keyboard glyph */}
        <rect x="0.5" y="0.5" width="13" height="10" rx="1.5" stroke="currentColor" />
        <rect x="2" y="2.2" width="1.4" height="1.4" fill="currentColor" />
        <rect x="4.2" y="2.2" width="1.4" height="1.4" fill="currentColor" />
        <rect x="6.4" y="2.2" width="1.4" height="1.4" fill="currentColor" />
        <rect x="8.6" y="2.2" width="1.4" height="1.4" fill="currentColor" />
        <rect x="10.8" y="2.2" width="1.4" height="1.4" fill="currentColor" />
        <rect x="2" y="4.6" width="1.4" height="1.4" fill="currentColor" />
        <rect x="4.2" y="4.6" width="1.4" height="1.4" fill="currentColor" />
        <rect x="6.4" y="4.6" width="1.4" height="1.4" fill="currentColor" />
        <rect x="8.6" y="4.6" width="1.4" height="1.4" fill="currentColor" />
        <rect x="10.8" y="4.6" width="1.4" height="1.4" fill="currentColor" />
        <rect x="3" y="7" width="8" height="1.6" rx="0.5" fill="currentColor" />
      </svg>
    </button>
  );
}
